import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchIntegratedAssessment } from "../../../store/slices/integratedAssessmentSlice";
import api from "../../../api";
import logo from "../../../assets/images/logo-samplee.png";

export default function IntegratedAssessmentFormPrint() {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // 1. Redux Store se currentRecord aur loading state select karein
    // Note: slice name 'integratedAssessments' store.js ke mutabiq check karlein
    const { currentRecord, isLoading, error } = useSelector(
        (state) => state.integratedAssessments || {}
    );

    // 2. Component load hote hi ID ke base par record fetch karein
    useEffect(() => {
        if (id) {
            dispatch(fetchIntegratedAssessment(id));
        }
    }, [id, dispatch]);

    // 3. Auto-print logic: Jab data load ho jaye aur loading false ho
    useEffect(() => {
        if (currentRecord && !isLoading) {
            const timer = setTimeout(() => {
                window.print();
            }, 1000); // 1 second delay taake images/styles properly load ho jayein

            // Print ke baad optional: wapis list page par jana
            window.onafterprint = () => navigate(-1);

            return () => {
                clearTimeout(timer);
                window.onafterprint = null;
            };
        }
    }, [currentRecord, isLoading, navigate]);

    // Read-only Family Meeting Minutes (latest 5) for this case/patient — shown, not stored here.
    const [fmmNotes, setFmmNotes] = useState([]);
    useEffect(() => {
        const caseId = currentRecord?.case_id;
        const patientId = currentRecord?.patient_id;
        if (!caseId && !patientId) return;
        let active = true;
        api.get("/family-meeting-minutes/for-integrated", { params: { case_id: caseId, patient_id: patientId, limit: 5 } })
            .then((res) => { if (active) setFmmNotes(res.data?.notes || []); })
            .catch(() => { if (active) setFmmNotes([]); });
        return () => { active = false; };
    }, [currentRecord]);

    // 4. Loading State View
    if (isLoading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <div className="text-center">
                    <div className="spinner-border text-primary mb-2"></div>
                    <div>Loading Assessment Details...</div>
                </div>
            </div>
        );
    }

    // 5. Error Handling View
    if (error) {
        return (
            <div className="p-5 text-center text-danger">
                <h4>Data Fetching Error</h4>
                <p>{error}</p>
                <button className="btn btn-primary" onClick={() => navigate(-1)}>Go Back</button>
            </div>
        );
    }

    // 6. Record Not Found View
    if (!currentRecord) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <div>No record found for ID: {id}</div>
            </div>
        );
    }

    // Data constant for mapping (matching your existing 'd' variable)
    const d = currentRecord;

    // Helper functions
    const formatArray = (arr) => {
        if (!arr) return "";
        if (Array.isArray(arr)) return arr.join(", ");
        return arr; // If already a string
    };

    const formatYesNo = (value) => (value === "1" || value === true || value === "Yes" || value === "yes") ? "✓" : "";

    // ===== New one-to-one sections: Cognitive Assessment + Discharge Planning (Updated) =====
    const cog = d.cognitive_assessment || null;
    const dp = d.discharge_planning || null;
    const AUDIT_KEYS = ['id', 'integrated_assessment_id', 'status', 'created_by', 'updated_by', 'deleted_by', 'created_at', 'updated_at', 'deleted_at'];
    // Show a section only if at least one meaningful (non-audit, non-auto-computed, non-empty) field is filled.
    const sectionFilled = (obj) => {
        if (!obj) return false;
        return Object.entries(obj).some(([k, v]) => {
            if (AUDIT_KEYS.includes(k)) return false;
            if (/_score$/.test(k) || k === 'total_score' || k === 'interpretation') return false; // auto-computed
            if (v === null || v === undefined || v === '' || v === false || v === 0 || v === '0') return false;
            if (Array.isArray(v) && v.length === 0) return false;
            return true;
        });
    };
    const showCog = sectionFilled(cog);
    const showDp = sectionFilled(dp);
    const cogScore = (v, max) => (v !== null && v !== undefined && v !== '') ? ` (Score: ${v}/${max})` : '';

    return (
        <>
            <style jsx global>{`
                * { box-sizing: border-box !important; }
                body { font-family: Arial, sans-serif !important; font-size: 11pt !important; line-height: 1.5 !important; color: #000 !important; background: #fff !important; margin: 0 !important; padding: 0 !important; }
                @page {
                    size: auto;
                    margin: 10mm !important;
                }
                .print-container { padding: 10px !important; position: relative !important; }
                .master-print-table {
                    width: 100% !important;
                    border-collapse: collapse !important;
                    border: none !important;
                }
                .master-print-table > tbody > tr > td,
                .master-print-table > tfoot > tr > td {
                    border: none !important;
                    padding: 0 !important;
                }
                .print-footer {
                    text-align: center !important;
                    font-size: 0.85rem !important;
                    color: #555 !important;
                    border-top: 1px solid #ccc !important;
                    padding-top: 8px !important;
                    padding-bottom: 8px !important;
                    margin-top: 15px !important;
                }
                .logo { position: absolute !important; top: 10px !important; left: 10px !important; z-index: 1000 !important; }
                .logo img { height: 70px !important; width: auto !important; }
                .header h3 { font-size: 1.5em !important; font-weight: bold !important; text-align: center !important; border-bottom: 1.5px solid #383838 !important; padding-bottom: 15px !important; margin-bottom: 10px !important; }
                .header h6 { font-size: 1.2em !important; text-align: center !important; margin-bottom: 5px !important; font-weight: 600 !important; }
                table { width: 100% !important; border-collapse: collapse !important; margin: 10px 0 !important; font-size: 0.95em !important; }
                td { border: 1px solid #000 !important; padding: 7px 10px !important; vertical-align: top !important; }
                .label { background: #f0f0f0 !important; width: 15% !important; }
                .checkbox { display: inline-flex !important; align-items: center !important; justify-content: center !important; width: 14px !important; height: 14px !important; border: 1px solid #000 !important; margin: 0 5px 0 5px !important; vertical-align: text-bottom !important; font-size: 10px !important; line-height: 1 !important; }
                .checked::before { content: "✓" !important; font-weight: bold !important; font-size: 11px !important; position: relative !important; bottom: 5px !important; left: 1px !important; }
                .checkbox-group { display: flex !important; flex-wrap: wrap !important; gap: 10px !important; margin-top: 5px !important; }
                .checkbox-grid-2 { display: grid !important; grid-template-columns: 1fr 1fr !important; gap: 8px !important; margin-top: 5px !important; }
                .checkbox-grid-3 { display: grid !important; grid-template-columns: 1fr 1fr 1fr !important; gap: 8px !important; margin-top: 5px !important; }
                .checkbox-item { display: flex !important; align-items: center !important; }
                .section { font-size: 1.2em !important; font-weight: 600 !important; margin: 25px 0 8px !important; }
                .center { text-align: center !important; }
                .area { border: 1px solid #000 !important; min-height: 70px !important; padding: 8px !important; }
                .no-print { display: block !important; }
                @media print { 
                    body { padding: 10px !important; } 
                    .print-container { padding: 15px !important; }
                    .no-print { display: none !important; }
                    .page-break { page-break-before: always !important; }
                }
            `}</style>

            <div className="logo">
                <img src={logo} alt="Logo" />
            </div>

            <table className="master-print-table">
                <tbody>
                    <tr>
                        <td>
                            <div className="print-container">
                                <div className="header">
                                    <h3>THE RECOVERY HOUSE</h3>
                                    <h6>Integrated Initial Assessment Form</h6>
                                </div>

                {/* Basic Information */}
                <table style={{ marginTop: "20px" }}>
                    <tbody>
                        <tr>
                            <td width="33%">MR #: <u>{d.mr_number || ''}</u></td>
                            <td width="33%">Date: <u>{d.date || ''}</u></td>
                            <td width="34%">Time: <u>{d.time || ''}</u></td>
                        </tr>
                    </tbody>
                </table>

                <div className="section">Sociodemographic Detail</div>
                <table>
                    <tbody>
                        <tr>
                            <td className="label">Name</td>
                            <td colSpan={2}><strong>{d.name || ''}</strong></td>
                            <td className="label">Age</td>
                            <td><strong>{d.age || ''}</strong></td>
                            <td className="label">Gender</td>
                            <td><strong>{d.gender || ''}</strong></td>
                        </tr>
                        <tr>
                            <td className="label">Occupation</td>
                            <td><strong>{d.occupation || ''}</strong></td>
                            <td className="label">Marital Status</td>
                            <td><strong>{d.marital_status || ''}</strong></td>
                            <td className="label" colSpan={2}>Source of Referral</td>
                            <td><strong>{d.source_of_referral || ''}</strong></td>
                        </tr>
                        <tr>
                            <td className="label" colSpan={2}>Informant (Reliability)</td>
                            <td><strong>{d.informant || ''}</strong></td>
                            <td className="label">Co morbid</td>
                            <td colSpan={3}><strong>{d.comorbid || ''}</strong></td>
                        </tr>
                        <tr>
                            <td colSpan={3}>Consent for History + Rehab</td>
                            <td colSpan={4}>
                                Client <span className="checkbox">{d.consent_type === 'Client' ? '✓' : ''}</span> &nbsp;&nbsp;
                                Family <span className="checkbox">{d.consent_type === 'Family' ? '✓' : ''}</span> &nbsp;&nbsp;
                                Guardian <span className="checkbox">{d.consent_type === 'Guardian' ? '✓' : ''}</span>
                                {d.consent_reason && <span> — Reason: <u>{d.consent_reason}</u></span>}
                            </td>
                        </tr>
                    </tbody>
                </table>

                <div className="section">Clinical Findings & Presenting Details</div>
                <table>
                    <tbody>
                        <tr>
                            <td className="label" style={{ fontWeight: 'bold', textAlign: 'center' }}>Presenting Complaints</td>
                        </tr>
                        <tr>
                            <td style={{ padding: '6px 8px' }}>{d.presenting_complaints || ''}</td>
                        </tr>
                        <tr>
                            <td className="label" style={{ fontWeight: 'bold', textAlign: 'center' }}>Psycho Social Stressor/s</td>
                        </tr>
                        <tr>
                            <td style={{ padding: '6px 8px' }}>{d.psycho_social_stressors || ''}</td>
                        </tr>
                        <tr>
                            <td className="label" style={{ fontWeight: 'bold', textAlign: 'center' }}>Functional Adjustment</td>
                        </tr>
                        <tr>
                            <td style={{ padding: '6px 8px' }}>{d.functional_adjustment || ''}</td>
                        </tr>
                        <tr>
                            <td className="label" style={{ fontWeight: 'bold', textAlign: 'center' }}>Vegetative Functions</td>
                        </tr>
                        <tr>
                            <td style={{ padding: '6px 8px' }}>{d.vegetative_functions || ''}</td>
                        </tr>
                        <tr>
                            <td className="label" style={{ fontWeight: 'bold', textAlign: 'center' }}>Current Treatment</td>
                        </tr>
                        <tr>
                            <td style={{ padding: '6px 8px' }}>{d.current_treatment || ''}</td>
                        </tr>
                        <tr>
                            <td className="label" style={{ fontWeight: 'bold', textAlign: 'center' }}>Allergies</td>
                        </tr>
                        <tr>
                            <td style={{ padding: '6px 8px' }}>{d.allergies || ''}</td>
                        </tr>
                    </tbody>
                </table>

                <div className="section">Past Psychiatric History</div>
                <table>
                    <tbody>
                        <tr>
                            <td className="label">Duration</td>
                            <td>{d.duration || ''}</td>
                            <td className="label">Episodes</td>
                            <td>{d.episodes || ''}</td>
                            <td className="label">ECT</td>
                            <td>{d.ect || ''}</td>
                            <td className="label">Course</td>
                            <td>{d.course || ''}</td>
                        </tr>
                        <tr>
                            <td className="label">Compliance</td>
                            <td>{d.compliance || ''}</td>
                            <td className="label">Admission</td>
                            <td>{d.admission || ''}</td>
                            <td className="label">OPD F/U</td>
                            <td>{d.opd_fu || ''}</td>
                            <td className="label">LAI</td>
                            <td>{d.lai || ''}</td>
                        </tr>
                        <tr>
                            <td className="label" colSpan={2}>EPS/Severe Side Effects</td>
                            <td colSpan={6}>{d.eps_side_effects || ''}</td>
                        </tr>
                    </tbody>
                </table>

                <div className="section">Risk Assessment as per SOP's</div>
                <table>
                    <thead>
                        <tr>
                            <td className="label">Risk Type</td>
                            <td className="label">LOW (Green)</td>
                            <td className="label">MEDIUM (Yellow)</td>
                            <td className="label">HIGH (Red)</td>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>ROH: Risk of Homicidal</td>
                            <td className="center">{d.roh === 'Low' ? '✓' : ''}</td>
                            <td className="center">{d.roh === 'Medium' ? '✓' : ''}</td>
                            <td className="center">{d.roh === 'High' ? '✓' : ''}</td>
                        </tr>
                        <tr>
                            <td>ROS: Risk of Suicide</td>
                            <td className="center">{d.ros === 'Low' ? '✓' : ''}</td>
                            <td className="center">{d.ros === 'Medium' ? '✓' : ''}</td>
                            <td className="center">{d.ros === 'High' ? '✓' : ''}</td>
                        </tr>
                        <tr>
                            <td>ROV: Risk of Violence</td>
                            <td className="center">{d.rov === 'Low' ? '✓' : ''}</td>
                            <td className="center">{d.rov === 'Medium' ? '✓' : ''}</td>
                            <td className="center">{d.rov === 'High' ? '✓' : ''}</td>
                        </tr>
                        <tr>
                            <td>ROE: Risk of Elopement</td>
                            <td className="center">{d.roe === 'Low' ? '✓' : ''}</td>
                            <td className="center">{d.roe === 'Medium' ? '✓' : ''}</td>
                            <td className="center">{d.roe === 'High' ? '✓' : ''}</td>
                        </tr>
                        <tr>
                            <td>ROF: Risk of Falls</td>
                            <td className="center">{d.rof === 'Low' ? '✓' : ''}</td>
                            <td className="center">{d.rof === 'Medium' ? '✓' : ''}</td>
                            <td className="center">{d.rof === 'High' ? '✓' : ''}</td>
                        </tr>
                        <tr>
                            <td>ROVU: Risk of Vulnerability</td>
                            <td className="center">{d.rovu === 'Low' ? '✓' : ''}</td>
                            <td className="center">{d.rovu === 'Medium' ? '✓' : ''}</td>
                            <td className="center">{d.rovu === 'High' ? '✓' : ''}</td>
                        </tr>
                    </tbody>
                </table>

                <table style={{ marginTop: '10px' }}>
                    <tbody>
                        <tr>
                            <td colSpan={2}><strong>Separate Detail Forms:</strong></td>
                        </tr>
                        <tr>
                            <td width="50%">Client at risk of suicide</td>
                            <td>{d.risk_of_suicide || 'N/A'}</td>
                        </tr>
                        <tr>
                            <td>Client at risk of violence</td>
                            <td>{d.risk_of_violence || 'N/A'}</td>
                        </tr>
                        <tr>
                            <td>Safety Care Plan (at discharge)</td>
                            <td>{d.time_of_discharge || 'N/A'}</td>
                        </tr>
                    </tbody>
                </table>
            </div>


            {/* PAGE 2 */}
            

            <div className="print-container">
                <div className="header">
                    <h6>Integrated Initial Assessment Form (Page 2)</h6>
                </div>

                {/* Background & History */}
                {d.part_one && (
                    <>
                        <div className="section">Background & History Details</div>
                        <table>
                            <tbody>
                                <tr>
                                    <td className="label" style={{ fontWeight: 'bold', textAlign: 'center' }}>Medical / Surgical History</td>
                                </tr>
                                <tr>
                                    <td style={{ padding: '6px 8px' }}>{d.part_one.medical_surgical_history || ''}</td>
                                </tr>
                                <tr>
                                    <td className="label" style={{ fontWeight: 'bold', textAlign: 'center' }}>Personal History</td>
                                </tr>
                                <tr>
                                    <td style={{ padding: '6px 8px' }}>{d.part_one.personal_history || ''}</td>
                                </tr>
                                <tr>
                                    <td className="label" style={{ fontWeight: 'bold', textAlign: 'center' }}>Educational History</td>
                                </tr>
                                <tr>
                                    <td style={{ padding: '6px 8px' }}>{d.part_one.educational_history || ''}</td>
                                </tr>
                                <tr>
                                    <td className="label" style={{ fontWeight: 'bold', textAlign: 'center' }}>Occupational History</td>
                                </tr>
                                <tr>
                                    <td style={{ padding: '6px 8px' }}>{d.part_one.occupational_history || ''}</td>
                                </tr>
                                <tr>
                                    <td className="label" style={{ fontWeight: 'bold', textAlign: 'center' }}>Spiritual History</td>
                                </tr>
                                <tr>
                                    <td style={{ padding: '6px 8px' }}>{d.part_one.spiritual_history || ''}</td>
                                </tr>
                                <tr>
                                    <td className="label" style={{ fontWeight: 'bold', textAlign: 'center' }}>Marital History</td>
                                </tr>
                                <tr>
                                    <td style={{ padding: '6px 8px' }}>{d.part_one.marital_history || ''}</td>
                                </tr>
                                <tr>
                                    <td className="label" style={{ fontWeight: 'bold', textAlign: 'center' }}>Legal Issues</td>
                                </tr>
                                <tr>
                                    <td style={{ padding: '6px 8px' }}>{d.part_one.legal_issues || ''}</td>
                                </tr>
                                <tr>
                                    <td className="label" style={{ fontWeight: 'bold', textAlign: 'center' }}>Pre-Morbid Personality</td>
                                </tr>
                                <tr>
                                    <td style={{ padding: '6px 8px' }}>{d.part_one.pre_morbid_personality || ''}</td>
                                </tr>
                                <tr>
                                    <td className="label" style={{ fontWeight: 'bold', textAlign: 'center' }}>Substance Use</td>
                                </tr>
                                <tr>
                                    <td style={{ padding: '6px 8px' }}>{d.part_one.substance_use || ''}</td>
                                </tr>
                                <tr>
                                    <td className="label" style={{ fontWeight: 'bold', textAlign: 'center' }}>Family History</td>
                                </tr>
                                <tr>
                                    <td style={{ padding: '6px 8px' }}>{d.part_one.family_history || ''}</td>
                                </tr>
                            </tbody>
                        </table>

                        {d.part_one.family_origin && (
                            <table>
                                <tbody>
                                    <tr>
                                        <td colSpan={4}><strong>Family of Origin:</strong> {d.part_one.family_origin}</td>
                                    </tr>
                                </tbody>
                            </table>
                        )}

                        <div className="section">General Physical Examination</div>
                        <table>
                            <tbody>
                                <tr>
                                    <td className="label" width="20%">General Appearance</td>
                                    <td colSpan={3}>{d.part_one.general_appearance || ''}</td>
                                </tr>
                                <tr>
                                    <td className="label">Cranial Nerve</td>
                                    <td>{d.part_one.cranial_nerve || ''}</td>
                                    <td className="label">Sensory System</td>
                                    <td>{d.part_one.sensory_system || ''}</td>
                                </tr>
                                <tr>
                                    <td className="label">Motor System</td>
                                    <td>{d.part_one.motor_system || ''}</td>
                                    <td className="label">Meningeal Irritation</td>
                                    <td>{d.part_one.meningeal_irritation || ''}</td>
                                </tr>
                                <tr>
                                    <td className="label">Cerebellar Signs</td>
                                    <td colSpan={3}>{d.part_one.cerebellar_signs || ''}</td>
                                </tr>
                            </tbody>
                        </table>
                        <div className="section">Physical Signs</div>
                        <table>
                            <thead>
                                <tr>
                                    <td className="label"><strong>Sign</strong></td>
                                    <td className="label"><strong>Y/N</strong></td>
                                    <td className="label"><strong>Sign</strong></td>
                                    <td className="label"><strong>Y/N</strong></td>
                                    <td className="label"><strong>Sign</strong></td>
                                    <td className="label"><strong>Y/N</strong></td>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>Pallor</td>
                                    <td>{d.part_one.pallor || ''}</td>
                                    <td>Clubbing</td>
                                    <td>{d.part_one.clubing || ''}</td>
                                    <td>Dehydration</td>
                                    <td>{d.part_one.dehydration || ''}</td>
                                </tr>
                                <tr>
                                    <td>Jaundice</td>
                                    <td>{d.part_one.jaundice || ''}</td>
                                    <td>Thyroid</td>
                                    <td>{d.part_one.thyroid || ''}</td>
                                    <td>Edema</td>
                                    <td>{d.part_one.edema || ''}</td>
                                </tr>
                                <tr>
                                    <td>Cyanosis</td>
                                    <td>{d.part_one.cyanosis || ''}</td>
                                    <td>Lymph Nodes</td>
                                    <td>{d.part_one.lymph_nodes || ''}</td>
                                    <td>Abnormal Movement</td>
                                    <td>{d.part_one.abnormal_movement_or_tremors || ''}</td>
                                </tr>
                                <tr>
                                    <td>Cut Marks</td>
                                    <td>{d.part_one.cut_marks || ''}</td>
                                    <td>Burn Marks</td>
                                    <td>{d.part_one.burn_marks || ''}</td>
                                    <td></td>
                                    <td></td>
                                </tr>
                            </tbody>
                        </table>

                        <div className="section">Central Nervous System</div>
                        <table>
                            <thead>
                                <tr>
                                    <td rowSpan="2"></td>
                                    <td className="label center" colSpan="2"><strong>RIGHT</strong></td>
                                    <td className="label center" colSpan="2"><strong>LEFT</strong></td>
                                </tr>
                                <tr>
                                    <td className="label"><strong>Upper Limb</strong></td>
                                    <td className="label"><strong>Lower Limb</strong></td>
                                    <td className="label"><strong>Upper Limb</strong></td>
                                    <td className="label"><strong>Lower Limb</strong></td>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td><strong>BULK</strong></td>
                                    <td>{d.part_one.bulk_right_upper || ''}</td>
                                    <td>{d.part_one.bulk_right_lower || ''}</td>
                                    <td>{d.part_one.bulk_left_upper || ''}</td>
                                    <td>{d.part_one.bulk_left_lower || ''}</td>
                                </tr>
                                <tr>
                                    <td><strong>TONE</strong></td>
                                    <td>{d.part_one.tone_right_upper || ''}</td>
                                    <td>{d.part_one.tone_right_lower || ''}</td>
                                    <td>{d.part_one.tone_left_upper || ''}</td>
                                    <td>{d.part_one.tone_left_lower || ''}</td>
                                </tr>
                                <tr>
                                    <td><strong>POWER</strong></td>
                                    <td>{d.part_one.power_right_upper || ''}</td>
                                    <td>{d.part_one.power_right_lower || ''}</td>
                                    <td>{d.part_one.power_left_upper || ''}</td>
                                    <td>{d.part_one.power_left_lower || ''}</td>
                                </tr>
                                <tr>
                                    <td><strong>REFLEXES</strong></td>
                                    <td>{d.part_one.reflexes_right_upper || ''}</td>
                                    <td>{d.part_one.reflexes_right_lower || ''}</td>
                                    <td>{d.part_one.reflexes_left_upper || ''}</td>
                                    <td>{d.part_one.reflexes_left_lower || ''}</td>
                                </tr>
                            </tbody>
                        </table>
                    </>
                )}
            </div>


            {/* PAGE 3 */}
            

            <div className="print-container">
                <div className="header">
                    <h6>Integrated Initial Assessment Form (Page 3)</h6>
                </div>

                {d.part_two && (
                    <>
                        <div className="section">Systemic Examination</div>
                        <table>
                            <tbody>
                                <tr>
                                    <td colSpan={4}><strong>Respiratory System</strong></td>
                                </tr>
                                <tr>
                                    <td className="label" width="20%">Inspection</td>
                                    <td width="30%">{d.part_two.respiratory_inspection || ''}</td>
                                    <td className="label" width="20%">Palpation</td>
                                    <td width="30%">{d.part_two.respiratory_palpation || ''}</td>
                                </tr>
                                <tr>
                                    <td className="label">Percussion</td>
                                    <td>{d.part_two.respiratory_percussion || ''}</td>
                                    <td className="label">Auscultation</td>
                                    <td>{d.part_two.respiratory_auscultation || ''}</td>
                                </tr>
                                <tr>
                                    <td colSpan={4}><strong>Abdominal Examination</strong></td>
                                </tr>
                                <tr>
                                    <td className="label">Inspection</td>
                                    <td>{d.part_two.abdominal_inspection || ''}</td>
                                    <td className="label">Palpation</td>
                                    <td>{d.part_two.abdominal_palpation || ''}</td>
                                </tr>
                                <tr>
                                    <td className="label">Percussion</td>
                                    <td>{d.part_two.abdominal_percussion || ''}</td>
                                    <td className="label">Auscultation</td>
                                    <td>{d.part_two.abdominal_auscultation || ''}</td>
                                </tr>
                                <tr>
                                    <td colSpan={4}><strong>Cardiovascular System</strong></td>
                                </tr>
                                <tr>
                                    <td className="label">Inspection</td>
                                    <td>{d.part_two.cardio_inspection || ''}</td>
                                    <td className="label">Palpation</td>
                                    <td>{d.part_two.cardio_palpation || ''}</td>
                                </tr>
                                <tr>
                                    <td className="label">Percussion</td>
                                    <td>{d.part_two.cardio_percussion || ''}</td>
                                    <td className="label">Auscultation</td>
                                    <td>{d.part_two.cardio_auscultation || ''}</td>
                                </tr>
                                <tr>
                                    <td colSpan={4}><strong>EPS (Extrapyramidal Symptoms)</strong></td>
                                </tr>
                                <tr>
                                    <td className="label">Akathisia</td>
                                    <td>{d.part_two.akathisia || ''}</td>
                                    <td className="label">Dystonia</td>
                                    <td>{d.part_two.dystonia || ''}</td>
                                </tr>
                                <tr>
                                    <td className="label">TD</td>
                                    <td>{d.part_two.td || ''}</td>
                                    <td className="label">Parkinson's</td>
                                    <td>{d.part_two.parkinsons || ''}</td>
                                </tr>
                            </tbody>
                        </table>

                        <div className="section">Diagnosis Details</div>
                        <table>
                            <tbody>
                                <tr>
                                    <td className="label" width="25%">Provisional Diagnosis</td>
                                    <td colSpan={3}>{d.part_two.provisional_diagnosis || ''}</td>
                                </tr>
                                <tr>
                                    <td className="label">Differential Diagnosis</td>
                                    <td colSpan={3}>{d.part_two.differential_diagnosis || ''}</td>
                                </tr>
                                <tr>
                                    <td className="label">Details</td>
                                    <td colSpan={3}>{d.part_two.diagnosis_treatment_details || ''}</td>
                                </tr>
                                <tr>
                                    <td colSpan={4}>
                                        <strong>Consent taken from:</strong> &nbsp;
                                        Client <span className="checkbox">{formatYesNo(d.part_two.consent_taken_client)}</span> &nbsp;&nbsp;
                                        Family <span className="checkbox">{formatYesNo(d.part_two.consent_taken_family)}</span> &nbsp;&nbsp;
                                        Guardian <span className="checkbox">{formatYesNo(d.part_two.consent_taken_guardian)}</span>
                                        {d.part_two.consent_taken_term && <span> — Reason: <u>{d.part_two.consent_taken_term}</u></span>}
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        <div className="section">Management Plan</div>
                        <table>
                            <tbody>
                                <tr>
                                    <td className="label" width="30%">Approximate Length of Stay</td>
                                    <td>{d.part_two.approx_length_of_stay || ''}</td>
                                </tr>
                            </tbody>
                        </table>

                        {d.problem_list && d.problem_list.length > 0 && (
                            <table>
                                <tbody>
                                    <tr>
                                        <td><strong>Problem List:</strong></td>
                                    </tr>
                                    {d.problem_list.map((problem, idx) => (
                                        <tr key={idx}>
                                            <td>{idx + 1}. {problem.problem}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                        <table>
                            <tbody>
                                <tr >
                                    <td colSpan={2}><strong>Treatment Plan:</strong></td>
                                </tr>
                                <tr>
                                    <td className="label" width="25%">Diagnostic Workup</td>
                                    <td><div>{d.part_two.diagnostic_workup || ''}</div></td>
                                </tr>
                                <tr>
                                    <td className="label">Pharmacotherapy</td>
                                    <td><div>{d.part_two.pharmacotherapy || ''}</div></td>
                                </tr>
                                <tr>
                                    <td className="label">Others (ECT, Psychotherapy)</td>
                                    <td><div>{d.part_two.treatment_others || ''}</div></td>
                                </tr>
                                <tr>
                                    <td className="label">Consultants</td>
                                    <td><div>{d.part_two.consultants || ''}</div></td>
                                </tr>
                            </tbody>
                        </table>
                        <table style={{ marginTop: '15px' }}>
                            <tbody>
                                <tr>
                                    <td colSpan={2}><strong>Resident & Consultant Details:</strong></td>
                                </tr>
                                <tr>
                                    <td width="50%">
                                        <strong>Resident Name:</strong> {d.part_two.resident_name || ''}<br />
                                        <strong>Cell #:</strong> {d.part_two.resident_cell_number || ''}<br />
                                        <strong>Date:</strong> {d.part_two.resident_sign_date || ''} &nbsp;
                                        <strong>Time:</strong> {d.part_two.resident_time || ''}
                                    </td>
                                    <td width="50%">
                                        <strong>Consultant:</strong> {d.part_two.consultant_doctors_name || ''}<br />
                                        <strong>Date:</strong> {d.part_two.consultant_sign_date || ''} &nbsp;
                                        <strong>Time:</strong> {d.part_two.consultant_time || ''}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </>
                )}
            </div>


            {/* PAGE 4 - NURSING ASSESSMENT */}
            

            <div className="print-container">
                <div className="header">
                    <h6>Integrated Initial Assessment Form (Page 4)</h6>
                </div>


                {d.part_two && (
                    <>
                        <div className="section">Nursing Assessment</div>
                        <table>
                            <tbody>
                                <tr>
                                    <td className="label" width="20%">Unit</td>
                                    <td width="30%">{d.part_two.unit || ''}</td>
                                    <td className="label" width="20%">Date of Admission</td>
                                    <td width="30%">{d.part_two.date_of_admission || ''}</td>
                                </tr>
                                <tr>
                                    <td className="label">Time</td>
                                    <td>{d.part_two.time_of_admission || ''}</td>
                                    <td className="label">Type</td>
                                    <td>{d.part_two.type_of_admission || ''}</td>
                                </tr>
                                <tr>
                                    <td className="label">Mode of Transportation</td>
                                    <td>{d.part_two.mode_of_transportation || ''}</td>
                                    <td className="label">Accompanied by</td>
                                    <td>{d.part_two.accompanied_by || ''}</td>
                                </tr>
                                <tr>
                                    <td className="label">ID Band</td>
                                    <td colSpan={3}>{d.part_two.id_band || ''}</td>
                                </tr>
                            </tbody>
                        </table>
                        <table>
                            <tbody>
                                <tr>
                                    <td className="label" width="20%">DOB</td>
                                    <td width="30%">{d.part_two.nursing_age || ''}</td>
                                    <td className="label" width="20%">Weight (Kg)</td>
                                    <td width="30%">{d.part_two.weight || ''}</td>
                                </tr>
                                <tr>
                                    <td className="label">Height (cm)</td>
                                    <td>{d.part_two.height || ''}</td>
                                    <td className="label">BP (mmhg)</td>
                                    <td>{d.part_two.blood_pressure || ''}</td>
                                </tr>
                                <tr>
                                    <td className="label">Temperature (°C)</td>
                                    <td>{d.part_two.temperature || ''}</td>
                                    <td className="label">Pulse (bpm)</td>
                                    <td>{d.part_two.pulse || ''}</td>
                                </tr>
                                <tr>
                                    <td className="label">Resp Rate (/mm)</td>
                                    <td>{d.part_two.resp_rate || ''}</td>
                                    <td className="label">O2 Saturation (%)</td>
                                    <td>{d.part_two.o2_saturation || ''}</td>
                                </tr>
                            </tbody>
                        </table>

                        <table>
                            <tbody>
                                <tr>
                                    <td className="label" width="25%">Reason for Admission</td>
                                    <td><div>{d.part_two.reason_for_admission || ''}</div></td>
                                </tr>
                            </tbody>
                        </table>
                        <div className="section">Patient Safety & Infection Control</div>
                        <table>
                            <tbody>
                                <tr>
                                    <td><strong>Communicable Disease:</strong> &nbsp;
                                        {d.part_two.communicable_disease && formatArray(d.part_two.communicable_disease).split(',').map((item, idx) => (
                                            <span key={idx}>
                                                {item.trim()} <span className="checkbox">✓</span> &nbsp;&nbsp;
                                            </span>
                                        ))}
                                    </td>
                                </tr>
                                {d.part_two.communicable_none && (
                                    <tr>
                                        <td>None (specify): <u>{d.part_two.communicable_none}</u></td>
                                    </tr>
                                )}
                                {d.part_two.communicable_other && (
                                    <tr>
                                        <td>Other (specify): <u>{d.part_two.communicable_other}</u></td>
                                    </tr>
                                )}
                            </tbody>
                        </table>

                        <table>
                            <tbody>
                                <tr>
                                    <td className="label" width="25%">Isolation Type</td>
                                    <td>{d.part_two.isolation_type || ''}</td>
                                </tr>
                                {d.part_two.isolation_other && (
                                    <tr>
                                        <td className="label">Other (specify)</td>
                                        <td>{d.part_two.isolation_other}</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>

                        <div className="section">Personal Belongings / Valuables</div>
                        <table>
                            <thead>
                                <tr>
                                    <td className="label"><strong>Item</strong></td>
                                    <td className="label"><strong>Status</strong></td>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>Eye Glasses / Contact Lenses</td>
                                    <td>{d.part_two.eye_glasses || ''}</td>
                                </tr>
                                <tr>
                                    <td>Hearing Aids</td>
                                    <td>{d.part_two.hearing_aids || ''}</td>
                                </tr>
                                <tr>
                                    <td>Dentures</td>
                                    <td>{d.part_two.dentures || ''}</td>
                                </tr>
                                <tr>
                                    <td>Jewelry / Expensive Item</td>
                                    <td>{d.part_two.jewelry || ''}</td>
                                </tr>
                                <tr>
                                    <td>Others</td>
                                    <td>{d.part_two.others || ''}</td>
                                </tr>
                            </tbody>
                        </table>
                    </>
                )}
            </div>


            {/* PAGE 5 - MDR & PAIN ASSESSMENT */}
            

            <div className="print-container">
                <div className="header">
                    <h6>Integrated Initial Assessment Form (Page 5)</h6>
                </div>


                {d.part_three && (
                    <>
                        <div className="section">Multi Drug Resistant Organism</div>
                        <table>
                            <tbody>
                                <tr>
                                    <td colSpan={4}>
                                        <strong>MDR Organisms:</strong>
                                        <div className="checkbox-grid-3">
                                            {d.part_three.mdr_organisms && formatArray(d.part_three.mdr_organisms).split(',').map((item, idx) => (
                                                <span key={idx} className="checkbox-item">
                                                    {item.trim()} <span className="checkbox">✓</span>
                                                </span>
                                            ))}
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td className="label center"><strong>Patient/Family informed about hospital's Patient Valuable Policy</strong></td>
                                </tr>
                                <tr>
                                    <td>{d.part_three.informed_valuable_policy || ''}</td>
                                </tr>
                            </tbody>
                        </table>

                        <div className="section">Pain Assessment</div>
                        <table>
                            <tbody>
                                <tr>
                                    <td className="label" width="25%">Presence of Pain</td>
                                    <td width="25%">{d.part_three.presence_of_pain || ''}</td>
                                    <td className="label" width="25%">Pain Score (0-10)</td>
                                    <td width="25%">{d.part_three.pain_score || ''}</td>
                                </tr>
                                <tr>
                                    <td colSpan={4}>
                                        <strong>Pain Scale Used:</strong>
                                        <div className="checkbox-grid-3">
                                            {d.part_three.pain_scale_used && formatArray(d.part_three.pain_scale_used).split(',').map((item, idx) => (
                                                <span key={idx} className="checkbox-item">
                                                    {item.trim()} <span className="checkbox">✓</span>
                                                </span>
                                            ))}
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td className="label">Location (Primary site)</td>
                                    <td colSpan={3}>{d.part_three.pain_location || ''}</td>
                                </tr>
                            </tbody>
                        </table>
                        <div style={{ fontSize: '8px', marginTop: '5px', fontStyle: 'italic' }}>
                            Note: If pain score is {'>'} 4, refer Pain Flow Sheet
                        </div>
                    </>
                )}
            </div>


            {/* PAGE 6 - FUNCTIONAL ASSESSMENT */}
            

            <div className="print-container">
                <div className="header">
                    <h6>Integrated Initial Assessment Form (Page 6)</h6>
                </div>


                {d.part_four && (
                    <>
                        <div className="section">Functional Assessment</div>

                        <table>
                            <tbody>
                                <tr>
                                    <td colSpan={6}><strong>Cognition / Perception Pattern</strong></td>
                                </tr>
                                <tr>
                                    <td colSpan={3} className="label">Level of Conscious</td>
                                    <td colSpan={3}>{d.part_four.level_of_conscious || ''}</td>
                                </tr>
                                <tr>
                                    <td className="label">Sensory</td>
                                    <td>{d.part_four.sensory || ''}</td>

                                    {d.part_four.sensory_others && (
                                        <>
                                            <td className="label">Sensory (specify)</td>
                                            <td colSpan={3}>{d.part_four.sensory_others}</td>
                                        </>
                                    )}
                                </tr>
                                <tr>
                                    <td className="label">Vision</td>
                                    <td>{d.part_four.vision || ''}</td>
                                    {d.part_four.vision_others && (
                                        <>
                                            <td className="label">Vision (specify)</td>
                                            <td colSpan={3}>{d.part_four.vision_others}</td>
                                        </>
                                    )}
                                </tr>
                                <tr>
                                    <td className="label">Hearing</td>
                                    <td>{d.part_four.hearing || ''}</td>

                                    {d.part_four.hearing_others && (
                                        <>
                                            <td className="label">Hearing (specify)</td>
                                            <td colSpan={3}>{d.part_four.hearing_others}</td>
                                        </>
                                    )}
                                </tr>
                                <tr>
                                    <td className="label">Speech</td>
                                    <td colSpan={3}>{d.part_four.speech || ''}</td>

                                    {d.part_four.speech_others && (
                                        <>
                                            <td className="label">Speech (specify)</td>
                                            <td colSpan={3}>{d.part_four.speech_others}</td>
                                        </>
                                    )}
                                </tr>
                            </tbody>
                        </table>

                        <table>
                            <tbody>
                                <tr>
                                    <td>
                                        <strong>Self Caring:</strong>
                                        <div className="checkbox-grid-3">
                                            {d.part_four.self_caring && formatArray(d.part_four.self_caring).split(',').map((item, idx) => (
                                                <span key={idx} className="checkbox-item">
                                                    {item.trim()} <span className="checkbox">✓</span>
                                                </span>
                                            ))}
                                        </div>
                                    </td>
                                </tr>
                                {d.part_four.self_caring_other && (
                                    <tr>
                                        <td>Other (specify): <u>{d.part_four.self_caring_other}</u></td>
                                    </tr>
                                )}
                            </tbody>
                        </table>

                        <table>
                            <tbody>
                                <tr>
                                    <td>
                                        <strong>Musculoskeletal:</strong>
                                        <div className="checkbox-grid-3">
                                            {d.part_four.musculoskeletal && formatArray(d.part_four.musculoskeletal).split(',').map((item, idx) => (
                                                <span key={idx} className="checkbox-item">
                                                    {item.trim()} <span className="checkbox">✓</span>
                                                </span>
                                            ))}
                                        </div>
                                    </td>
                                </tr>
                                {d.part_four.musculoskeletal_other && (
                                    <tr>
                                        <td>Other (specify): <u>{d.part_four.musculoskeletal_other}</u></td>
                                    </tr>
                                )}
                            </tbody>
                        </table>

                        <table>
                            <tbody>
                                <tr>
                                    <td>
                                        <strong>Use of Assisting Equipment:</strong>
                                        <div className="checkbox-grid-3">
                                            {d.part_four.assisting && formatArray(d.part_four.assisting).split(',').map((item, idx) => (
                                                <span key={idx} className="checkbox-item">
                                                    {item.trim()} <span className="checkbox">✓</span>
                                                </span>
                                            ))}
                                        </div>
                                    </td>
                                </tr>
                                {d.part_four.assisting_other && (
                                    <tr>
                                        <td>Other (specify): <u>{d.part_four.assisting_other}</u></td>
                                    </tr>
                                )}
                            </tbody>
                        </table>

                        <div style={{ fontSize: '8px', marginTop: '5px', fontStyle: 'italic' }}>
                            Note: Any Gray area checked requires Physician to be notified for relevant referral
                        </div>
                    </>
                )}
            </div>


            {/* PAGE 7 - ORIENTATION TO UNIT */}
            

            <div className="print-container">
                <div className="header">
                    <h6>Integrated Initial Assessment Form (Page 7)</h6>
                </div>

                {d.part_four && (
                    <>
                        <div className="section">Orientation to Unit</div>

                        <table>
                            <tbody>
                                <tr>
                                    <td>
                                        <strong>Items Given on Admission:</strong>
                                        <div className="checkbox-grid-3">
                                            {d.part_four.admission_items && formatArray(d.part_four.admission_items).split(',').map((item, idx) => (
                                                <span key={idx} className="checkbox-item">
                                                    {item.trim()} <span className="checkbox">✓</span>
                                                </span>
                                            ))}
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        <table>
                            <tbody>
                                <tr>
                                    <td colSpan={4}>
                                        <strong>Cognition / Perception Pattern:</strong>
                                        <div className="checkbox-grid-3">
                                            {d.part_four.cognition && formatArray(d.part_four.cognition).split(',').map((item, idx) => (
                                                <span key={idx} className="checkbox-item">
                                                    {item.trim()} <span className="checkbox">✓</span>
                                                </span>
                                            ))}
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td className="label" width="25%">Client Level</td>
                                    <td>{d.part_four.client_level || ''}</td>

                                    {d.part_four.client_level_others && (
                                        <>
                                            <td className="label">Level (specify)</td>
                                            <td>{d.part_four.client_level_others}</td>
                                        </>
                                    )}
                                </tr>
                            </tbody>
                        </table>

                        <div className="section">Orientation Check Questions</div>
                        <table>
                            <thead>
                                <tr>
                                    <td className="label"><strong>Question</strong></td>
                                    <td className="label"><strong>Yes/No</strong></td>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>Room Allocation and Bed allotment, water cooler</td>
                                    <td>{d.part_four.room_allocation || ''}</td>
                                </tr>
                                <tr>
                                    <td>Inform Client about medicine administration and laboratory testing</td>
                                    <td>{d.part_four.inform_medicine || ''}</td>
                                </tr>
                                <tr>
                                    <td>Guide about family meetings and calling schedules</td>
                                    <td>{d.part_four.guide_family_meetings || ''}</td>
                                </tr>
                                <tr>
                                    <td>Give detail information about consultant doctors</td>
                                    <td>{d.part_four.info_consultants || ''}</td>
                                </tr>
                                <tr>
                                    <td>Authorize hair cutting procedure due to hygiene concern</td>
                                    <td>{d.part_four.authorize_hair_cutting || ''}</td>
                                </tr>
                                <tr>
                                    <td>Maintain isolation for contagious disease</td>
                                    <td>{d.part_four.isolation_contagious || ''}</td>
                                </tr>
                            </tbody>
                        </table>
                    </>
                )}
            </div>



            {/* PAGE 8 - NUTRITION ASSESSMENT */}
            

            <div className="print-container">
                <div className="header">
                    <h6>Integrated Initial Assessment Form (Page 8)</h6>
                </div>

                {d.part_four && (
                    <>
                        <div className="section">Nutrition Assessment & Dietitian Referral</div>

                        <table>
                            <tbody>
                                <tr>
                                    <td className="label">Weight (Kg)</td>
                                    <td>{d.part_four.weight_kg || ''}</td>
                                    <td className="label">Height (cm)</td>
                                    <td>{d.part_four.height_cm || ''}</td>
                                    <td className="label">BMI</td>
                                    <td>{d.part_four.bmi || ''}</td>
                                </tr>
                            </tbody>
                        </table>

                        <div className="section">Nutrition Risk Questions</div>
                        <table>
                            <thead>
                                <tr>
                                    <td className="label"><strong>Question</strong></td>
                                    <td className="label"><strong>Yes/No</strong></td>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>BMI is Less than or Equal to {'<'}18.5</td>
                                    <td>{d.part_four.bmi_less_18 || ''}</td>
                                </tr>
                                <tr>
                                    <td>BMI is equal to or greater than {'≥'}25 (obesity ASIAN Classification)</td>
                                    <td>{d.part_four.bmi_greater_25 || ''}</td>
                                </tr>
                                <tr>
                                    <td>Has the client lost weight within last (3) months?</td>
                                    <td>{d.part_four.lost_weight_3_months || ''}</td>
                                </tr>
                                <tr>
                                    <td>Recent history of hospitalization requiring intensive care</td>
                                    <td>{d.part_four.recent_hospitalization || ''}</td>
                                </tr>
                            </tbody>
                        </table>

                        <table>
                            <tbody>
                                <tr>
                                    <td colSpan={4}>
                                        <strong>Mandatory Dietitian Referral Conditions:</strong>
                                        <div className="checkbox-grid-2">
                                            {d.part_four.dietitian_referral_conditions && formatArray(d.part_four.dietitian_referral_conditions).split(',').map((item, idx) => (
                                                <span key={idx} className="checkbox-item">
                                                    {item.trim()} <span className="checkbox">✓</span>
                                                </span>
                                            ))}
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td className="label">Refer to Dietitian / Notes</td>
                                    <td><div>{d.part_four.refer_dietitian || ''}</div></td>
                                </tr>
                            </tbody>
                        </table>
                    </>
                )}
            </div>



            {/* PAGE 9 - RISK ASSESSMENT (NURSING) */}
            

            <div className="print-container">
                <div className="header">
                    <h6>Integrated Initial Assessment Form (Page 9)</h6>
                </div>

                {d.part_four && (
                    <>
                        <div className="section">Risk Assessment as per SOP's (Filled by Nursing Staff)</div>
                        <table className="risk-table">
                            <thead>
                                <tr>
                                    <td className="label">Risk Type</td>
                                    <td className="label">LOW (Green)</td>
                                    <td className="label">MEDIUM (Yellow)</td>
                                    <td className="label">HIGH (Red)</td>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>ROH: Risk of Homicidal</td>
                                    <td>{d.part_four.roh_nursing === 'Low' ? '✓' : ''}</td>
                                    <td>{d.part_four.roh_nursing === 'Medium' ? '✓' : ''}</td>
                                    <td>{d.part_four.roh_nursing === 'High' ? '✓' : ''}</td>
                                </tr>
                                <tr>
                                    <td>ROS: Risk of Suicide</td>
                                    <td>{d.part_four.ros_nursing === 'Low' ? '✓' : ''}</td>
                                    <td>{d.part_four.ros_nursing === 'Medium' ? '✓' : ''}</td>
                                    <td>{d.part_four.ros_nursing === 'High' ? '✓' : ''}</td>
                                </tr>
                                <tr>
                                    <td>ROV: Risk of Violence</td>
                                    <td>{d.part_four.rov_nursing === 'Low' ? '✓' : ''}</td>
                                    <td>{d.part_four.rov_nursing === 'Medium' ? '✓' : ''}</td>
                                    <td>{d.part_four.rov_nursing === 'High' ? '✓' : ''}</td>
                                </tr>
                                <tr>
                                    <td>ROE: Risk of Elopement</td>
                                    <td>{d.part_four.roe_nursing === 'Low' ? '✓' : ''}</td>
                                    <td>{d.part_four.roe_nursing === 'Medium' ? '✓' : ''}</td>
                                    <td>{d.part_four.roe_nursing === 'High' ? '✓' : ''}</td>
                                </tr>
                                <tr>
                                    <td>ROF: Risk of Falls</td>
                                    <td>{d.part_four.rof_nursing === 'Low' ? '✓' : ''}</td>
                                    <td>{d.part_four.rof_nursing === 'Medium' ? '✓' : ''}</td>
                                    <td>{d.part_four.rof_nursing === 'High' ? '✓' : ''}</td>
                                </tr>
                                <tr>
                                    <td>ROVU: Risk of Vulnerability</td>
                                    <td>{d.part_four.rovu_nursing === 'Low' ? '✓' : ''}</td>
                                    <td>{d.part_four.rovu_nursing === 'Medium' ? '✓' : ''}</td>
                                    <td>{d.part_four.rovu_nursing === 'High' ? '✓' : ''}</td>
                                </tr>
                                <tr>
                                    <td colSpan={4} className="label"><strong>Separate Detail Forms:</strong></td>
                                </tr>
                                <tr>
                                    <td>Risk of Suicide</td>
                                    <td>{d.part_four.suicide_form_nursing === 'Low' ? '✓' : ''}</td>
                                    <td>{d.part_four.suicide_form_nursing === 'Medium' ? '✓' : ''}</td>
                                    <td>{d.part_four.suicide_form_nursing === 'High' ? '✓' : ''}</td>
                                </tr>
                                <tr>
                                    <td>Risk of Violence</td>
                                    <td>{d.part_four.violence_form_nursing === 'Low' ? '✓' : ''}</td>
                                    <td>{d.part_four.violence_form_nursing === 'Medium' ? '✓' : ''}</td>
                                    <td>{d.part_four.violence_form_nursing === 'High' ? '✓' : ''}</td>
                                </tr>
                                <tr>
                                    <td>Safety Care Plan Form</td>
                                    <td>{d.part_four.safety_care_plan_nursing === 'Low' ? '✓' : ''}</td>
                                    <td>{d.part_four.safety_care_plan_nursing === 'Medium' ? '✓' : ''}</td>
                                    <td>{d.part_four.safety_care_plan_nursing === 'High' ? '✓' : ''}</td>
                                </tr>
                            </tbody>
                        </table>



                    </>
                )}
            </div>

            {/* PAGE 10 - MENTAL STATE EXAMINATION */}
            

            <div className="print-container">
                <div className="header">
                    <h6>Integrated Initial Assessment Form (Page 10)</h6>
                </div>

                {d.part_four && (
                    <>
                        <div className="section">Mental State Examination / MSE</div>

                        <table>
                            <tbody>
                                <tr>
                                    <td className="label" width="25%"><strong>Appearance</strong></td>
                                    <td width="25%">{d.part_four.appearance || ''}</td>
                                    <td className="label" width="25%"><strong>Behavior</strong></td>
                                    <td width="25%">{d.part_four.behavior || ''}</td>
                                </tr>
                                <tr>
                                    <td className="label"><strong>Speech</strong></td>
                                    <td>{d.part_four.speech_mse || ''}</td>
                                    <td className="label"><strong>Mood / Affect</strong></td>
                                    <td>{d.part_four.mood_affect || ''}</td>
                                </tr>
                                <tr>
                                    <td className="label"><strong>Thought Process</strong></td>
                                    <td>{d.part_four.thought_process || ''}</td>
                                    <td className="label"><strong>Thought Content</strong></td>
                                    <td>{d.part_four.thought_content || ''}</td>
                                </tr>
                                <tr>
                                    <td className="label"><strong>Insight</strong></td>
                                    <td>{d.part_four.insight || ''}</td>
                                    <td className="label"><strong>Memory</strong></td>
                                    <td>{d.part_four.memory || ''}</td>
                                </tr>
                                <tr>
                                    <td className="label"><strong>Self Esteem</strong></td>
                                    <td>{d.part_four.self_esteem || ''}</td>
                                    <td className="label"><strong>Hopelessness</strong></td>
                                    <td>{d.part_four.hopelessness || ''}</td>
                                </tr>
                                <tr>
                                    <td className="label"><strong>Worthlessness</strong></td>
                                    <td>{d.part_four.worthlessness || ''}</td>
                                    <td className="label"><strong>Coping</strong></td>
                                    <td>{d.part_four.coping || ''}</td>
                                </tr>
                            </tbody>
                        </table>

                        <div className="section">Socioeconomic</div>
                        <table>
                            <tbody>
                                <tr>
                                    <td className="label" width="25%"><strong>Marital Status</strong></td>
                                    <td width="25%">{d.part_four.marital_status_socio || ''}</td>
                                    <td className="label" width="25%"><strong>No of Children</strong></td>
                                    <td width="25%">{d.part_four.no_of_children || ''}</td>
                                </tr>
                                <tr>
                                    <td className="label"><strong>Living Situation</strong></td>
                                    <td colSpan={3}>{d.part_four.living_situation || ''}</td>
                                </tr>
                                {d.part_four.living_situation_specify && (
                                    <tr>
                                        <td className="label"><strong>Living Situation (specify)</strong></td>
                                        <td colSpan={3}>{d.part_four.living_situation_specify}</td>
                                    </tr>
                                )}
                                <tr>
                                    <td className="label"><strong>Work Situation</strong></td>
                                    <td colSpan={3}>{d.part_four.work_situation || ''}</td>
                                </tr>
                                {d.part_four.occupation && (
                                    <tr>
                                        <td className="label"><strong>Occupation</strong></td>
                                        <td colSpan={3}>{d.part_four.occupation}</td>
                                    </tr>
                                )}
                                <tr>
                                    <td className="label"><strong>Habit</strong></td>
                                    <td colSpan={3}>{d.part_four.habit || ''}</td>
                                </tr>
                                {d.part_four.habit_specify && (
                                    <tr>
                                        <td className="label"><strong>Habit (specify)</strong></td>
                                        <td colSpan={3}>{d.part_four.habit_specify}</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </>
                )}
            </div>


            {/* PAGE 11 - RELIGIOUS & REVIEW OF SYSTEMS */}
            

            <div className="print-container">
                <div className="header">
                    <h6>Integrated Initial Assessment Form (Page 11)</h6>
                </div>

                {d.part_five && (
                    <>
                        <div className="section">Religious / Culture / Spiritual Needs</div>

                        <table>
                            <tbody>
                                {/* Row 1: Religion and the conditional 'Other' field */}
                                <tr>
                                    <td className="label" width="25%"><strong>Religion</strong></td>
                                    <td width="25%">
                                        {d.part_five.religion || ''}
                                        {/* Inline display of 'Other' if it exists to keep the row balanced */}
                                        {d.part_five.religion_other && ` (${d.part_five.religion_other})`}
                                    </td>
                                    <td className="label" width="25%"><strong>Religious Practices</strong></td>
                                    <td width="25%">{d.part_five.religious_practices || ''}</td>
                                </tr>

                                {/* Conditional Rows: Only show if Religious Practices is 'Yes' */}
                                {d.part_five.religious_practices === 'Yes' && (
                                    <>
                                        <tr>
                                            <td className="label"><strong>Privacy</strong></td>
                                            <td>{d.part_five.privacy || ''}</td>
                                            <td className="label"><strong>Food</strong></td>
                                            <td>{d.part_five.food || ''}</td>
                                        </tr>
                                        <tr>
                                            <td className="label"><strong>Gender Regalia</strong></td>
                                            <td>{d.part_five.gender_regalia || ''}</td>
                                            <td className="label"><strong>Other Values</strong></td>
                                            <td>{d.part_five.other_values || ''}</td>
                                        </tr>
                                    </>
                                )}
                            </tbody>
                        </table>

                        <div className="section">Review of Systems</div>

                        <table>
                            <tbody>
                                <tr>
                                    <td colSpan={4} className="center"><strong>Oral</strong></td>
                                </tr>
                                <tr>
                                    <td className="label" width="25%"><strong>Mouth / Tongue</strong></td>
                                    <td width="25%">{d.part_five.mouth_tongue || ''}</td>
                                    <td className="label" width="25%"><strong>Sign / Symptom</strong></td>
                                    <td width="25%">{d.part_five.oral_sign_symptom || ''}</td>
                                </tr>
                                {d.part_five.oral_sign_symptom_others && (
                                    <tr>
                                        <td className="label"><strong>Symptom (specify)</strong></td>
                                        <td colSpan={3}>{d.part_five.oral_sign_symptom_others}</td>
                                    </tr>
                                )}
                                <tr>
                                    <td className="label"><strong>NG / Gastric tube</strong></td>
                                    <td colSpan={3}>{d.part_five.ng_gastric || ''}</td>
                                </tr>
                                {d.part_five.ng_gastric_date && (
                                    <tr>
                                        <td className="label"><strong>NG Date</strong></td>
                                        <td colSpan={3}>{d.part_five.ng_gastric_date}</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>

                        <table>
                            <tbody>
                                <tr>
                                    <td colSpan={4} className="center"><strong>Skin</strong></td>
                                </tr>
                                <tr>
                                    <td className="label" width="25%"><strong>Appearance</strong></td>
                                    <td colSpan={3}>{d.part_five.skin_appearance || ''}</td>
                                </tr>
                                {d.part_five.skin_appearance_other && (
                                    <tr>
                                        <td className="label"><strong>Appearance (specify)</strong></td>
                                        <td colSpan={3}>{d.part_five.skin_appearance_other}</td>
                                    </tr>
                                )}
                                <tr>
                                    <td className="label"><strong>Temperature</strong></td>
                                    <td>{d.part_five.skin_temperature || ''}</td>
                                    <td className="label"><strong>Contagious</strong></td>
                                    <td>{d.part_five.skin_contagious || ''}</td>
                                </tr>
                                {d.part_five.skin_location && (
                                    <tr>
                                        <td className="label"><strong>Location</strong></td>
                                        <td colSpan={3}>{d.part_five.skin_location}</td>
                                    </tr>
                                )}
                                {d.part_five.skin_reasons && (
                                    <tr>
                                        <td className="label"><strong>Reasons</strong></td>
                                        <td colSpan={3}>{d.part_five.skin_reasons}</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>

                        <table>
                            <tbody>
                                <tr>
                                    <td colSpan={4} className="center"><strong>Cardiovascular</strong></td>
                                </tr>
                                <tr>
                                    <td className="label" width="25%"><strong>Pulse</strong></td>
                                    <td width="25%">{d.part_five.cardio_pulse || ''}</td>
                                    <td className="label" width="25%"><strong>Sign / Symptom</strong></td>
                                    <td width="25%">{d.part_five.cardio_sign_symptom || ''}</td>
                                </tr>
                                {d.part_five.cardio_sign_symptom_other && (
                                    <tr>
                                        <td className="label"><strong>Symptom (specify)</strong></td>
                                        <td colSpan={3}>{d.part_five.cardio_sign_symptom_other}</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>

                        <table>
                            <tbody>
                                <tr>
                                    <td colSpan={4} className="center"><strong>Respiratory</strong></td>
                                </tr>
                                <tr>
                                    <td className="label" width="25%"><strong>Respiration</strong></td>
                                    <td colSpan={3}>{d.part_five.respiration || ''}</td>
                                </tr>
                                {d.part_five.respiration_other && (
                                    <tr>
                                        <td className="label"><strong>Respiration (specify)</strong></td>
                                        <td colSpan={3}>{d.part_five.respiration_other}</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>

                        <table>
                            <tbody>
                                <tr>
                                    <td colSpan={4} className="center"><strong>Cough</strong></td>
                                </tr>
                                <tr>
                                    <td className="label"><strong>Cough</strong></td>
                                    <td colSpan={3}>{d.part_five.cough || ''}</td>
                                </tr>
                                {d.part_five.cough_other && (
                                    <tr>
                                        <td className="label"><strong>Cough (specify)</strong></td>
                                        <td colSpan={3}>{d.part_five.cough_other}</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </>
                )}
            </div>

            {/* PAGE 12 - ELIMINATION & NURSING NEEDS */}
            

            <div className="print-container">
                <div className="header">
                    <h6>Integrated Initial Assessment Form (Page 12)</h6>
                </div>

                {d.part_five && (
                    <>
                        <div className="section">Elimination</div>

                        <table>
                            <tbody>
                                <tr>
                                    <td colSpan={4} className="center"><strong>Bowel</strong></td>
                                </tr>
                                <tr>
                                    <td className="label" width="25%"><strong>Bowel Movement</strong></td>
                                    <td width="25%">{d.part_five.bowel_movement || ''}</td>
                                    <td className="label" width="25%"><strong>Sign / Symptom</strong></td>
                                    <td width="25%">{d.part_five.bowel_sign_symptom || ''}</td>
                                </tr>
                            </tbody>
                        </table>

                        <table>
                            <tbody>
                                <tr>
                                    <td colSpan={4} className="center"><strong>Urinary</strong></td>
                                </tr>
                                <tr>
                                    <td className="label" width="25%"><strong>Appearance</strong></td>
                                    <td width="25%">{d.part_five.urinary_appearance || ''}</td>
                                    <td className="label" width="25%"><strong>Sign / Symptom</strong></td>
                                    <td width="25%">{d.part_five.urinary_sign_symptom || ''}</td>
                                </tr>
                                <tr>
                                    <td className="label"><strong>Catheter Type</strong></td>
                                    <td>{d.part_five.catheter_type || ''}</td>
                                    <td className="label"><strong>Date of Insertion</strong></td>
                                    <td>{d.part_five.date_of_insertion || ''}</td>
                                </tr>
                                <tr>
                                    <td className="label"><strong>Others</strong></td>
                                    <td colSpan={3}>{d.part_five.urinary_others || ''}</td>
                                </tr>
                            </tbody>
                        </table>

                        <table>
                            <tbody>
                                <tr>
                                    <td colSpan={4} className="center"><strong>Obs / Gyn</strong></td>
                                </tr>
                                <tr>
                                    <td className="label" width="25%"><strong>Obs / Gyn</strong></td>
                                    <td width="25%">{d.part_five.obs_gyn || ''}</td>
                                    <td className="label" width="25%"><strong>Menstrual Cycle</strong></td>
                                    <td width="25%">{d.part_five.menstrual_cycle || ''}</td>
                                </tr>
                                <tr>
                                    <td className="label"><strong>Dysmenorrhea</strong></td>
                                    <td>{d.part_five.dysmenorrhea || ''}</td>
                                    <td className="label"><strong>Skin Changes</strong></td>
                                    <td>{d.part_five.skin_changes || ''}</td>
                                </tr>
                                <tr>
                                    <td className="label"><strong>Others</strong></td>
                                    <td colSpan={3}>{d.part_five.obs_gyn_others || ''}</td>
                                </tr>
                            </tbody>
                        </table>

                        {d.nursing_needs && d.nursing_needs.length > 0 && (
                            <>
                                <div className="section">Problem List / Nursing Needs Identified</div>
                                <table className="risk-table">
                                    <thead>
                                        <tr>
                                            <td className="label"><strong>#</strong></td>
                                            <td className="label"><strong>Nursing Need</strong></td>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {d.nursing_needs.map((need, idx) => (
                                            <tr key={idx}>
                                                <td width="10%">{idx + 1}</td>
                                                <td>{need.nursing_need}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </>
                        )}

                        <div className="section">Initial Assessment Signatures</div>
                        <table>
                            <tbody>
                                <tr>
                                    <td className="label" width="25%"><strong>Nursing Staff</strong></td>
                                    <td width="25%">{d.part_five.nursing_staff || ''}</td>
                                    <td className="label" width="25%"><strong>Checked by</strong></td>
                                    <td width="25%">{d.part_five.checked_by || ''}</td>
                                </tr>
                                <tr>
                                    <td className="label"><strong>Designation</strong></td>
                                    <td>{d.part_five.designation || ''}</td>
                                    <td className="label"><strong>Signature</strong></td>
                                    <td>{d.part_five.signature || ''}</td>
                                </tr>
                                <tr>
                                    <td className="label"><strong>Date</strong></td>
                                    <td>{d.part_five.nursing_date || ''}</td>
                                    <td className="label"><strong>Time</strong></td>
                                    <td>{d.part_five.nursing_time || ''}</td>
                                </tr>
                                <tr>
                                    <td className="label" width="25%"><strong>Head Nurse</strong></td>
                                    <td width="25%">{d.part_five.head_nurse || ''}</td>
                                    <td className="label" width="25%"><strong>Clinical Nurse Instructor</strong></td>
                                    <td width="25%">{d.part_five.clinical_nurse_instructor || ''}</td>
                                </tr>
                                <tr>
                                    <td className="label"><strong>Team Leader</strong></td>
                                    <td colSpan={3}>{d.part_five.team_leader || ''}</td>
                                </tr>
                            </tbody>
                        </table>
                    </>
                )}
            </div>

            {/* PAGE 13 - MONTHLY PROGRESS NOTES */}
            

            <div className="print-container">
                <div className="header">
                    <h6>Integrated Initial Assessment Form (Page 13)</h6>
                </div>

                <>
                    <div className="section">Monthly Progress Notes and Family Meeting Minutes (PRP Section)</div>
                    <div style={{ fontSize: '0.85em', fontStyle: 'italic', marginBottom: '8px' }}>
                        (Family Meeting Minutes of this case — latest 5)
                    </div>

                    {fmmNotes.length > 0 ? (
                        fmmNotes.map((note, idx) => (
                            <table key={note.id || idx} style={{ marginBottom: '15px' }}>
                                <tbody>
                                    <tr>
                                        <td className="center" colSpan={2} style={{ backgroundColor: '#f0f0f0', fontWeight: 'bold' }}>
                                            Meeting {idx + 1}{note.meeting_date ? ` — ${note.meeting_date}` : ''}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="label" width="25%">Report Written By</td>
                                        <td>{note.report_written_by || ''}</td>
                                    </tr>
                                    <tr>
                                        <td className="label">Family Members</td>
                                        <td>{(note.family_members || []).map((m) => m.name).filter(Boolean).join(', ') || ''}</td>
                                    </tr>
                                    <tr>
                                        <td className="label">Team Members</td>
                                        <td>{(note.team_members || []).map((m) => m.name).filter(Boolean).join(', ') || ''}</td>
                                    </tr>
                                    <tr>
                                        <td colSpan={2}><strong>Agenda</strong></td>
                                    </tr>
                                    <tr>
                                        <td colSpan={2} style={{ verticalAlign: 'top' }}>{note.agenda || ''}</td>
                                    </tr>
                                    <tr>
                                        <td colSpan={2}><strong>Summary of Discussion</strong></td>
                                    </tr>
                                    <tr>
                                        <td colSpan={2} style={{ verticalAlign: 'top' }}>{note.summary || ''}</td>
                                    </tr>
                                    <tr>
                                        <td colSpan={2}><strong>Action Plan Agreed Upon</strong></td>
                                    </tr>
                                    <tr>
                                        <td colSpan={2} style={{ verticalAlign: 'top' }}>{note.action_plan || ''}</td>
                                    </tr>
                                </tbody>
                            </table>
                        ))
                    ) : (
                        <div style={{ fontStyle: 'italic', color: '#666', marginBottom: '15px' }}>
                            No Family Meeting Minutes recorded for this case.
                        </div>
                    )}

                    <div className="section">Endorsement by Dr. / Additional Remarks</div>
                    {d.part_five && d.part_five.endorsement && (
                        <table>
                            <tbody>
                                <tr>
                                    <td colSpan={4} className="label"><strong>Endorsement / Remarks</strong></td>
                                </tr>
                                <tr>
                                    <td colSpan={4} style={{ height: '80px', verticalAlign: 'top' }}>
                                        {d.part_five.endorsement}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    )}
                </>
            </div>

            {/* PAGE 14 - INDIVIDUAL REHABILITATION PLAN */}
            

            <div className="print-container">
                <div className="header">
                    <h6>Integrated Initial Assessment Form (Page 14)</h6>
                </div>

                {d.part_five && (
                    <>
                        <div className="section">Individual Rehabilitation Plan</div>

                        <table>
                            <tbody>
                                <tr>
                                    <td className="label" width="20%"><strong>Name</strong></td>
                                    <td width="30%">{d.part_five.irp_name || ''}</td>
                                    <td className="label" width="20%"><strong>Date</strong></td>
                                    <td width="30%">{d.part_five.irp_date || ''}</td>
                                </tr>
                                <tr>
                                    <td className="label"><strong>Domain</strong></td>
                                    <td>{d.part_five.irp_domain || ''}</td>
                                    <td className="label"><strong>Rehab Goal #</strong></td>
                                    <td>{d.part_five.irp_rehab_goal || ''}</td>
                                </tr>
                            </tbody>
                        </table>

                        <table style={{ marginTop: '10px' }}>
                            <thead>
                                <tr>
                                    <td width="50%"><strong>Strengths (skills & resources possess)</strong></td>
                                    <td width="50%"><strong>Skills and Resources (needed)</strong></td>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td style={{ height: '100px', verticalAlign: 'top' }}>{d.part_five.irp_strengths || ''}</td>
                                    <td style={{ height: '100px', verticalAlign: 'top' }}>{d.part_five.irp_skills_needed || ''}</td>
                                </tr>
                            </tbody>
                        </table>

                        {d.obj_act_plan && d.obj_act_plan.length > 0 && (
                            <>
                                <div className="section">Objectives & Action Plan</div>
                                {d.obj_act_plan.map((obj, oIdx) => (
                                    <div key={oIdx} style={{ marginBottom: '15px' }}>
                                        <table style={{ marginBottom: '5px' }}>
                                            <tbody>
                                                <tr>
                                                    <td className="label" width="15%"><strong>Objective {oIdx + 1}</strong></td>
                                                    <td>{obj.objective}</td>
                                                    <td className="label" width="10%"><strong>Status</strong></td>
                                                    <td width="15%">{obj.action_status}</td>
                                                    <td className="label" width="10%"><strong>Date</strong></td>
                                                    <td width="15%">{obj.date}</td>
                                                </tr>
                                            </tbody>
                                        </table>

                                        {obj.step && obj.step.length > 0 && (
                                            <table className="risk-table">
                                                <thead>
                                                    <tr>
                                                        <td style={{ width: '30px' }}><strong>#</strong></td>
                                                        <td><strong>Action Step</strong></td>
                                                        <td><strong>Location</strong></td>
                                                        <td><strong>Frequency</strong></td>
                                                        <td><strong>Duration</strong></td>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {obj.step.map((action, aIdx) => (
                                                        <tr key={aIdx}>
                                                            <td>{aIdx + 1}</td>
                                                            <td>{action.action_step}</td>
                                                            <td>{action.location}</td>
                                                            <td>{action.frequency}</td>
                                                            <td>{action.duration}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        )}
                                    </div>
                                ))}
                            </>
                        )}

                        <div className="center" style={{ marginTop: '15px', fontSize: '13px', fontStyle: 'italic' }}>
                            I have participated in the development of this IRP goal and am in agreement.
                        </div>

                        <table style={{ marginTop: '10px' }}>
                            <tbody>
                                <tr>
                                    <td className="label" width="25%"><strong>Client's Signature</strong></td>
                                    <td width="25%">{d.part_five.irp_client_signature || ''}</td>
                                    <td className="label" width="25%"><strong>Date</strong></td>
                                    <td width="25%">{d.part_five.irp_client_date || ''}</td>
                                </tr>
                                <tr>
                                    <td className="label"><strong>Psych Rehab Practitioner's Signature</strong></td>
                                    <td>{d.part_five.irp_practitioner_signature || ''}</td>
                                    <td className="label"><strong>Date</strong></td>
                                    <td>{d.part_five.irp_practitioner_date || ''}</td>
                                </tr>
                            </tbody>
                        </table>
                    </>
                )}
            </div>

            {/* PAGE 15 - DISCHARGE PLANNING (old section — commented out per request; uncomment the block below to restore) */}
            {/*
            <div className="print-container">
                <div className="header">
                    <h6>Integrated Initial Assessment Form (Page 15)</h6>
                </div>

                {d.part_six && (
                    <>
                        <div className="section">Discharge Planning</div>

                        <table>
                            <tbody>
                                <tr>
                                    <td className="label" width="25%"><strong>Discharge Type</strong></td>
                                    <td width="25%">{d.part_six.discharge || ''}</td>
                                    <td className="label" width="25%"><strong>Discharge Instruction To</strong></td>
                                    <td width="25%">{d.part_six.discharge_instruction_to || ''}</td>
                                </tr>
                            </tbody>
                        </table>

                        <table style={{ marginTop: '10px' }}>
                            <thead>
                                <tr>
                                    <td className="center" width="50%"><strong>Special Instructions</strong></td>
                                    <td className="center" width="50%"><strong>Injections If any</strong></td>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td style={{ height: '80px', verticalAlign: 'top' }}>{d.part_six.special_instructions || ''}</td>
                                    <td style={{ height: '80px', verticalAlign: 'top' }}>{d.part_six.injections || ''}</td>
                                </tr>
                                <tr>
                                    <td className="center"><strong>Any Red Flag</strong></td>
                                    <td className="center"><strong>Risk Assessment at DC</strong></td>
                                </tr>
                                <tr>
                                    <td style={{ height: '80px', verticalAlign: 'top' }}>{d.part_six.any_red_flag || ''}</td>
                                    <td style={{ height: '80px', verticalAlign: 'top' }}>{d.part_six.risk_assessment_dc || ''}</td>
                                </tr>
                            </tbody>
                        </table>

                        <table style={{ marginTop: '10px' }}>
                            <tbody>
                                <tr>
                                    <td className="label" width="25%">Expected Discharge Date</td>
                                    <td width="25%">{d.part_six.expected_discharge_date || ''}</td>
                                    <td className="label" width="25%">Home Care By</td>
                                    <td width="25%">{d.part_six.home_care || ''}</td>
                                </tr>
                                {d.part_six.home_care_others_specify && (
                                    <tr>
                                        <td className="label">Home Care (specify)</td>
                                        <td colSpan={3}>{d.part_six.home_care_others_specify}</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>

                        <table style={{ marginTop: '10px' }}>
                            <tbody>
                                <tr>
                                    <td className="label" width="25%">Transportation</td>
                                    <td width="25%">{d.part_six.transportation || ''}</td>
                                    <td className="label" width="25%">Psychotherapy</td>
                                    <td width="25%">{d.part_six.psychotherapy || ''}</td>
                                </tr>
                                <tr>
                                    <td className="label">Long Term Care</td>
                                    <td>{d.part_six.long_term_care || ''}</td>
                                    <td className="label">Day Rehab</td>
                                    <td>{d.part_six.day_rehab || ''}</td>
                                </tr>
                                {d.part_six.day_rehab_days && (
                                    <tr>
                                        <td className="label">Days per week</td>
                                        <td colSpan={3}>{d.part_six.day_rehab_days}</td>
                                    </tr>
                                )}
                                <tr>
                                    <td className="label">Other Needs</td>
                                    <td colSpan={3}>{d.part_six.other_needs || ''}</td>
                                </tr>
                                {d.part_six.other_needs_specify && (
                                    <tr>
                                        <td className="label">Other Needs (specify)</td>
                                        <td colSpan={3}>{d.part_six.other_needs_specify}</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </>
                )}
            </div>
            */}

            {/* PAGE 16 - FAMILY COUNSELLING */}
            

            <div className="print-container">
                <div className="header">
                    <h6>Integrated Initial Assessment Form (Page 16)</h6>
                </div>

                {d.part_six && (
                    <>
                        <div className="section">Family Counselling filled by PRP's / Doctor</div>

                        <table className="risk-table">
                            <thead>
                                <tr>
                                    <td><strong>Education / Guidance Item</strong></td>
                                    <td style={{ width: '60px' }}><strong>Yes/No</strong></td>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>Educate the family about the client's illness condition</td>
                                    <td>{d.part_six.educate_illness || ''}</td>
                                </tr>
                                <tr>
                                    <td>Educate the family about the client's hygiene at home</td>
                                    <td>{d.part_six.educate_hygiene || ''}</td>
                                </tr>
                                <tr>
                                    <td>Educate the family about the medication and compliance</td>
                                    <td>{d.part_six.educate_medication || ''}</td>
                                </tr>
                                <tr>
                                    <td>If unusual behavior, they should follow up in OPD</td>
                                    <td>{d.part_six.unusual_behavior || ''}</td>
                                </tr>
                                <tr>
                                    <td>Educate about giving medicine and monitoring</td>
                                    <td>{d.part_six.giving_medicine || ''}</td>
                                </tr>
                                <tr>
                                    <td>Contact Doctor before next followup if needed</td>
                                    <td>{d.part_six.contact_doctor || ''}</td>
                                </tr>
                                <tr>
                                    <td>Client sharing with family</td>
                                    <td>{d.part_six.client_sharing || ''}</td>
                                </tr>
                                <tr>
                                    <td>Engage client in activities, games, family time</td>
                                    <td>{d.part_six.engaged_activities || ''}</td>
                                </tr>
                                <tr>
                                    <td>Trigger sign of relapse monitoring</td>
                                    <td>{d.part_six.trigger_relapse || ''}</td>
                                </tr>
                            </tbody>
                        </table>

                        <div className="section">Further Counselling</div>
                        <table style={{ marginTop: '10px' }}>
                            <tbody>
                                {/* Row 1: Sleep and Wake Times */}
                                <tr>
                                    <td className="label" colSpan={4}><strong>Maintaining regular sleep and wake times:</strong></td>
                                </tr>
                                <tr>
                                    <td colSpan={4} style={{ height: '60px', verticalAlign: 'top', padding: '8px' }}>
                                        {d.part_six.sleep_wake || ''}
                                    </td>
                                </tr>

                                {/* Row 2: Balanced Meals */}
                                <tr>
                                    <td className="label" colSpan={4}><strong>Eating balanced meals at regular times:</strong></td>
                                </tr>
                                <tr>
                                    <td colSpan={4} style={{ height: '60px', verticalAlign: 'top', padding: '8px' }}>
                                        {d.part_six.eating_meals || ''}
                                    </td>
                                </tr>

                                {/* Row 3: Physical Activity */}
                                <tr>
                                    <td className="label" colSpan={4}><strong>Engaging in regular physical activity or exercise:</strong></td>
                                </tr>
                                <tr>
                                    <td colSpan={4} style={{ height: '60px', verticalAlign: 'top', padding: '8px' }}>
                                        {d.part_six.physical_activity || ''}
                                    </td>
                                </tr>

                                {/* Row 4: Coping Strategies */}
                                <tr>
                                    <td className="label" colSpan={4}><strong>Using healthy coping strategies to manage stress:</strong></td>
                                </tr>
                                <tr>
                                    <td colSpan={4} style={{ height: '60px', verticalAlign: 'top', padding: '8px' }}>
                                        {d.part_six.coping_strategies || ''}
                                    </td>
                                </tr>

                                {/* Row 5: Preventing Relapse */}
                                <tr>
                                    <td className="label" colSpan={4}><strong>Preventing relapse through consistent routines and supports:</strong></td>
                                </tr>
                                <tr>
                                    <td colSpan={4} style={{ height: '60px', verticalAlign: 'top', padding: '8px' }}>
                                        {d.part_six.preventing_relapse || ''}
                                    </td>
                                </tr>

                                {/* Row 6: Socially Connected */}
                                <tr>
                                    <td className="label" colSpan={4}><strong>Staying socially connected with family, friends, or community:</strong></td>
                                </tr>
                                <tr>
                                    <td colSpan={4} style={{ height: '60px', verticalAlign: 'top', padding: '8px' }}>
                                        {d.part_six.socially_connected || ''}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </>
                )}
            </div>

            {/* PAGE 17 - ANTICIPATED DISCHARGE NEEDS */}
            

            <div className="print-container">
                <div className="header">
                    <h6>Integrated Initial Assessment Form (Page 17)</h6>
                </div>

                {d.part_six && (
                    <>
                        <div className="section">Anticipated Discharge Needs</div>
                        <div style={{ fontSize: '9px', marginBottom: '10px' }}>
                            Following Information/education has been imparted on the basis of anticipated discharge needs:
                        </div>

                        <table>
                            <tbody>
                                <tr>
                                    <td className="label" width="25%"><strong>Diet</strong></td>
                                    <td width="25%">{d.part_six.diet || ''}</td>
                                    <td className="label" width="25%"><strong>Take Home Medication</strong></td>
                                    <td width="25%">{d.part_six.take_home_medication || ''}</td>
                                </tr>
                                <tr>
                                    <td className="label"><strong>Compliance Treatment</strong></td>
                                    <td>{d.part_six.compliance_treatment || ''}</td>
                                    <td className="label"><strong>Followup Visit</strong></td>
                                    <td>{d.part_six.followup_visit || ''}</td>
                                </tr>
                                <tr>
                                    <td className="label"><strong>Effective Coping</strong></td>
                                    <td>{d.part_six.effective_coping || ''}</td>
                                    <td className="label"><strong>Hospital Emergency</strong></td>
                                    <td>{d.part_six.hospital_emergency || ''}</td>
                                </tr>
                                <tr>
                                    <td className="label"><strong>Activity at Home</strong></td>
                                    <td>{d.part_six.activity_home || ''}</td>
                                    <td className="label"><strong>Stress Management</strong></td>
                                    <td>{d.part_six.stress_management || ''}</td>
                                </tr>
                                <tr>
                                    <td className="label"><strong>Aggression Management</strong></td>
                                    <td>{d.part_six.aggression_management || ''}</td>
                                    <td className="label"><strong>Alarming Signs</strong></td>
                                    <td>{d.part_six.alarming_signs || ''}</td>
                                </tr>
                                <tr>
                                    <td className="label"><strong>Occupational Therapy</strong></td>
                                    <td>{d.part_six.occupational_therapy || ''}</td>
                                    <td className="label"><strong>Social Support</strong></td>
                                    <td>{d.part_six.social_support || ''}</td>
                                </tr>
                                <tr>
                                    <td className="label"><strong>Environmental Safety</strong></td>
                                    <td>{d.part_six.environmental_safety || ''}</td>
                                    <td className="label"><strong>Processgiver Role Strain</strong></td>
                                    <td>{d.part_six.process_giver_role_strain || ''}</td>
                                </tr>
                                <tr>
                                    <td className="label"><strong>Long Term Facilities</strong></td>
                                    <td>{d.part_six.long_term_facilities || ''}</td>
                                    <td className="label"><strong>Contact #</strong></td>
                                    <td>{d.part_six.contact_number || ''}</td>
                                </tr>
                                <tr>
                                    <td className="label"><strong>Others</strong></td>
                                    <td colSpan={3}>{d.part_six.others || ''}</td>
                                </tr>
                            </tbody>
                        </table>

                        <table style={{ marginTop: '15px' }}>
                            <tbody>
                                <tr>
                                    <td className="label" width="20%"><strong>Name / Signature</strong></td>
                                    <td width="30%">{d.part_six.name_signature || ''}</td>
                                    <td className="label" width="15%"><strong>Date</strong></td>
                                    <td width="15%">{d.part_six.signature_date || ''}</td>
                                    <td className="label" width="10%"><strong>Time</strong></td>
                                    <td width="10%">{d.part_six.signature_time || ''}</td>
                                </tr>
                            </tbody>
                        </table>
                    </>
                )}
            </div>

            {/* PAGE 18 - HOME CARE FUTURE SUPPORT & FOOTER */}
            

            <div className="print-container">
                <div className="header">
                    <h6>Integrated Initial Assessment Form (Page 18)</h6>
                </div>

                {d.part_six && (
                    <>
                        <div className="section">Home Care Future Support Plan</div>

                        <table style={{ marginTop: '10px' }}>
                            <tbody>
                                {/* 1. Possible Relapse Sign */}
                                <tr>
                                    <td className="label" colSpan={4}><strong>Possible Relapse Sign:</strong></td>
                                </tr>
                                <tr>
                                    <td colSpan={4} style={{ height: '60px', verticalAlign: 'top', padding: '8px' }}>
                                        {d.part_six.possible_relapse_sign || ''}
                                    </td>
                                </tr>

                                {/* 2. Recovery Goals */}
                                <tr>
                                    <td className="label" colSpan={4}><strong>Recovery Goals:</strong></td>
                                </tr>
                                <tr>
                                    <td colSpan={4} style={{ height: '60px', verticalAlign: 'top', padding: '8px' }}>
                                        {d.part_six.recovery_goals || ''}
                                    </td>
                                </tr>

                                {/* 3. Who to Contact */}
                                <tr>
                                    <td className="label" colSpan={4}><strong>Who to Contact:</strong></td>
                                </tr>
                                <tr>
                                    <td colSpan={4} style={{ height: '60px', verticalAlign: 'top', padding: '8px' }}>
                                        {d.part_six.who_to_contact || ''}
                                    </td>
                                </tr>

                                {/* 4. Where to Go in Crises */}
                                <tr>
                                    <td className="label" colSpan={4}><strong>Where to Go in Crises:</strong></td>
                                </tr>
                                <tr>
                                    <td colSpan={4} style={{ height: '60px', verticalAlign: 'top', padding: '8px' }}>
                                        {d.part_six.where_to_go_crises || ''}
                                    </td>
                                </tr>

                                {/* 5. Details of Medication */}
                                <tr>
                                    <td className="label" colSpan={4}><strong>Details of Medication:</strong></td>
                                </tr>
                                <tr>
                                    <td colSpan={4} style={{ height: '60px', verticalAlign: 'top', padding: '8px' }}>
                                        {d.part_six.details_medication || ''}
                                    </td>
                                </tr>

                                {/* 6. Detail of Comprehensive Treatment Plan */}
                                <tr>
                                    <td className="label" colSpan={4}><strong>Detail of Comprehensive Treatment Plan:</strong></td>
                                </tr>
                                <tr>
                                    <td colSpan={4} style={{ height: '60px', verticalAlign: 'top', padding: '8px' }}>
                                        {d.part_six.detail_treatment_plan || ''}
                                    </td>
                                </tr>

                                {/* 7. Physical Health Needs */}
                                <tr>
                                    <td className="label" colSpan={4}><strong>Physical Health Needs:</strong></td>
                                </tr>
                                <tr>
                                    <td colSpan={4} style={{ height: '60px', verticalAlign: 'top', padding: '8px' }}>
                                        {d.part_six.physical_health_needs || ''}
                                    </td>
                                </tr>

                                {/* 8. Admission Pathway */}
                                <tr>
                                    <td className="label" colSpan={4}><strong>Admission Pathway:</strong></td>
                                </tr>
                                <tr>
                                    <td colSpan={4} style={{ height: '60px', verticalAlign: 'top', padding: '8px' }}>
                                        {d.part_six.admission_pathway || ''}
                                    </td>
                                </tr>

                                {/* 9. Risk Assessment and Safety Plan */}
                                <tr>
                                    <td className="label" colSpan={4}><strong>Risk Assessment and Safety Plan:</strong></td>
                                </tr>
                                <tr>
                                    <td colSpan={4} style={{ height: '80px', verticalAlign: 'top', padding: '8px' }}>
                                        {d.part_six.risk_assessment_safety_plan || ''}
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        {/* --- Meta Data Tables (Review & Document Info) --- */}
                        <table style={{ marginTop: '15px' }}>
                            <tbody>
                                <tr>
                                    <td className="label" width="20%">Date of Review</td>
                                    <td width="20%">{d.part_six.date_of_review || ''}</td>
                                    <td className="label" width="20%">Doctor Name</td>
                                    <td width="40%">{d.part_six.doctor_name || ''}</td>
                                </tr>
                                <tr>
                                    <td className="label">Guardian/Client Name</td>
                                    <td colSpan={3}>{d.part_six.guardian_name || ''}</td>
                                </tr>
                            </tbody>
                        </table>

                        <table style={{ marginTop: '30px', fontSize: '10px' }}>
                            <tbody>
                                <tr>
                                    <td className="label" width="20%">Name of Documents</td>
                                    <td width="30%">{d.part_six.name_of_documents || ''}</td>
                                    <td className="label" width="20%">Effective Date</td>
                                    <td width="30%">{d.part_six.effective_date || ''}</td>
                                </tr>
                                <tr>
                                    <td className="label">Approved By</td>
                                    <td>{d.part_six.approved_by || ''}</td>
                                    <td className="label">Approved Date</td>
                                    <td>{d.part_six.approved_date || ''}</td>
                                </tr>
                                <tr>
                                    <td className="label">Next Revision By</td>
                                    <td>{d.part_six.next_revision_by || ''}</td>
                                    <td className="label">Next Revision Date</td>
                                    <td>{d.part_six.next_revision_date || ''}</td>
                                </tr>
                            </tbody>
                        </table>
                    </>
                )}
            </div>

            {/* ===================== NEW: Discharge Planning (Updated) ===================== */}
            {showDp && (
                <div className="print-container page-break">
                    <div className="header">
                        <h6>Integrated Initial Assessment Form — Discharge Planning (Updated)</h6>
                    </div>
                    <div className="section">Discharge Planning (Updated)</div>
                    <table>
                        <tbody>
                            <tr>
                                <td className="label" width="30%">Education Provided To</td>
                                <td colSpan={3}>{formatArray(dp.education_provided_to)}</td>
                            </tr>
                            <tr>
                                <td className="label">Recovery Goals</td>
                                <td colSpan={3} style={{ verticalAlign: 'top' }}>{dp.recovery_goals || ''}</td>
                            </tr>
                            <tr>
                                <td className="label">Possible Relapse Signs</td>
                                <td colSpan={3} style={{ verticalAlign: 'top' }}>{dp.possible_relapse_signs || ''}</td>
                            </tr>
                            <tr>
                                <td className="label">Who to Contact</td>
                                <td colSpan={3}>{dp.who_to_contact || ''}</td>
                            </tr>
                            <tr>
                                <td className="label">Where to Go in Crisis</td>
                                <td colSpan={3} style={{ verticalAlign: 'top' }}>{dp.where_to_go_crisis || ''}</td>
                            </tr>
                            <tr>
                                <td className="label">Red Flag Warnings</td>
                                <td colSpan={3} style={{ verticalAlign: 'top' }}>{dp.red_flag_warnings || ''}</td>
                            </tr>
                            <tr>
                                <td className="label" width="30%">Follow-up Psychiatrist Date</td>
                                <td colSpan={3}>{dp.followup_psychiatrist_date || ''}</td>
                            </tr>
                            <tr>
                                <td className="label">Physical Health Needs</td>
                                <td colSpan={3} style={{ verticalAlign: 'top' }}>{dp.physical_health_needs || ''}</td>
                            </tr>
                            <tr>
                                <td className="label">Special Instructions</td>
                                <td colSpan={3} style={{ verticalAlign: 'top' }}>{dp.special_instructions || ''}</td>
                            </tr>
                            <tr>
                                <td className="label">Transition For</td>
                                <td colSpan={3}>{formatArray(dp.transition_for)}{dp.transition_for_other ? ` — ${dp.transition_for_other}` : ''}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            )}

            {/* ===================== NEW: Cognitive Assessment ===================== */}
            {showCog && (
                <div className="print-container page-break">
                    <div className="header">
                        <h6>Integrated Initial Assessment Form — Cognitive Assessment</h6>
                    </div>
                    <div className="section">Cognitive Assessment</div>

                    <table>
                        <tbody>
                            <tr>
                                <td className="label" width="25%">Setting</td>
                                <td width="25%">{cog.setting || ''}</td>
                                <td className="label" width="25%">Level of Consciousness</td>
                                <td width="25%">{cog.level_of_consciousness || ''}</td>
                            </tr>
                        </tbody>
                    </table>

                    <div className="section" style={{ fontSize: '1.05em' }}>Orientation{cogScore(cog.orientation_score, 3)}</div>
                    <table>
                        <tbody>
                            <tr>
                                <td className="label" width="16%">Time</td><td width="17%">{cog.orientation_time || ''}</td>
                                <td className="label" width="16%">Place</td><td width="17%">{cog.orientation_place || ''}</td>
                                <td className="label" width="17%">Person</td><td width="17%">{cog.orientation_person || ''}</td>
                            </tr>
                        </tbody>
                    </table>

                    <div className="section" style={{ fontSize: '1.05em' }}>Attention &amp; Concentration{cogScore(cog.attention_score, 5)}</div>
                    <table>
                        <tbody>
                            <tr>
                                <td className="label" width="25%">Digit Span Forward</td><td width="25%">{cog.digit_span_forward || ''}</td>
                                <td className="label" width="25%">Digit Span Backward</td><td width="25%">{cog.digit_span_backward || ''}</td>
                            </tr>
                            <tr>
                                <td className="label">Serial 7s</td><td>{cog.serial_7s || ''}</td>
                                <td className="label">Months Backward</td><td>{cog.months_backward || ''}</td>
                            </tr>
                            <tr>
                                <td className="label">Distractibility</td><td colSpan={3}>{cog.distractibility || ''}</td>
                            </tr>
                        </tbody>
                    </table>

                    <div className="section" style={{ fontSize: '1.05em' }}>Memory{cogScore(cog.memory_score, 10)}</div>
                    <table>
                        <tbody>
                            <tr>
                                <td className="label" width="25%">Recall 3 Objects (Immediate)</td><td width="25%">{cog.recall_3_object_immediate || ''}</td>
                                <td className="label" width="25%">Recall (5 min)</td><td width="25%">{cog.recall_5_min || ''}</td>
                            </tr>
                            <tr>
                                <td className="label">Recent Events Recall</td><td>{cog.recent_events_recall || ''}</td>
                                <td className="label">Personal History</td><td>{cog.personal_history || ''}</td>
                            </tr>
                            <tr>
                                <td className="label">Past Events</td><td colSpan={3}>{cog.past_events || ''}</td>
                            </tr>
                        </tbody>
                    </table>

                    <div className="section" style={{ fontSize: '1.05em' }}>Language{cogScore(cog.language_score, 6)}</div>
                    <table>
                        <tbody>
                            <tr>
                                <td className="label" width="16%">Fluency</td><td width="17%">{cog.fluency || ''}</td>
                                <td className="label" width="16%">Comprehension</td><td width="17%">{cog.comprehension || ''}</td>
                                <td className="label" width="17%">Naming</td><td width="17%">{cog.naming || ''}</td>
                            </tr>
                            <tr>
                                <td className="label">Repetition</td><td>{cog.repetition || ''}</td>
                                <td className="label">Reading</td><td>{cog.reading || ''}</td>
                                <td className="label">Writing</td><td>{cog.writing || ''}</td>
                            </tr>
                        </tbody>
                    </table>

                    <div className="section" style={{ fontSize: '1.05em' }}>Executive Function{cogScore(cog.executive_score, 5)}</div>
                    <table>
                        <tbody>
                            <tr>
                                <td className="label" width="25%">Proverbs</td><td width="25%">{cog.proverbs || ''}</td>
                                <td className="label" width="25%">Similarities</td><td width="25%">{cog.similarities || ''}</td>
                            </tr>
                            <tr>
                                <td className="label">Social Judgment</td><td>{cog.social_judgment || ''}</td>
                                <td className="label">Test Judgment</td><td>{cog.test_judgment || ''}</td>
                            </tr>
                            <tr>
                                <td className="label">Task Planning</td><td colSpan={3}>{cog.task_planning || ''}</td>
                            </tr>
                        </tbody>
                    </table>

                    <div className="section" style={{ fontSize: '1.05em' }}>Perception</div>
                    <table>
                        <tbody>
                            <tr>
                                <td className="label" width="25%">Perception</td><td width="25%">{cog.perception || ''}</td>
                                <td className="label" width="25%">Type</td><td width="25%">{formatArray(cog.perception_type)}</td>
                            </tr>
                        </tbody>
                    </table>

                    <div className="section" style={{ fontSize: '1.05em' }}>Visuospatial{cogScore(cog.visuospatial_score, 3)}</div>
                    <table>
                        <tbody>
                            <tr>
                                <td className="label" width="16%">Shape Copying</td><td width="17%">{cog.shape_copying || ''}</td>
                                <td className="label" width="16%">Clock Drawing</td><td width="17%">{cog.clock_drawing || ''}</td>
                                <td className="label" width="17%">Spatial Orientation</td><td width="17%">{cog.spatial_orientation || ''}</td>
                            </tr>
                        </tbody>
                    </table>

                    <div className="section" style={{ fontSize: '1.05em' }}>Psychomotor, Insight &amp; Functional Status</div>
                    <table>
                        <tbody>
                            <tr>
                                <td className="label" width="25%">Psychomotor</td><td width="25%">{cog.psychomotor || ''}</td>
                                <td className="label" width="25%">Insight</td><td width="25%">{cog.insight || ''}</td>
                            </tr>
                            <tr>
                                <td className="label">Functional Status</td><td colSpan={3}>{cog.functional_status || ''}</td>
                            </tr>
                        </tbody>
                    </table>

                    <div className="section" style={{ fontSize: '1.05em' }}>Risk Indicators</div>
                    <table>
                        <tbody>
                            <tr>
                                <td width="20%"><span className={`checkbox ${cog.risk_impulsivity ? 'checked' : ''}`}></span> Impulsivity</td>
                                <td width="20%"><span className={`checkbox ${cog.risk_poor_judgment ? 'checked' : ''}`}></span> Poor Judgment</td>
                                <td width="20%"><span className={`checkbox ${cog.risk_wandering ? 'checked' : ''}`}></span> Wandering</td>
                                <td width="20%"><span className={`checkbox ${cog.risk_confusion ? 'checked' : ''}`}></span> Confusion</td>
                                <td width="20%"><span className={`checkbox ${cog.risk_non_adherence ? 'checked' : ''}`}></span> Non-adherence</td>
                            </tr>
                        </tbody>
                    </table>

                    <div className="section" style={{ fontSize: '1.05em' }}>Overall Cognitive Score</div>
                    <table>
                        <tbody>
                            <tr>
                                <td className="label" width="25%">Total Score</td>
                                <td width="25%"><strong>{(cog.total_score !== null && cog.total_score !== undefined) ? cog.total_score : ''} / 32</strong></td>
                                <td className="label" width="25%">Interpretation</td>
                                <td width="25%"><strong>{cog.interpretation || ''}</strong></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            )}

            <div className="footer" style={{
                marginTop: '40px',
                display: 'flex !important',
                flexDirection: 'column !important',
                alignItems: 'center !important', // Flex centering
                justifyContent: 'center !important',
                width: '100% !important',
                paddingTop: '10px',
                borderTop: '2px solid #000'
            }}>
                <p style={{
                    textAlign: 'center !important',
                    width: '100% !important',
                    display: 'block !important',
                    margin: '0 auto !important'
                }}>
                    156 Bangalore Town, Tipu Sultan Road, Karachi, Pakistan | Ph: 021-34546364-65-66
                </p>
                <p style={{
                    textAlign: 'center !important',
                    width: '100% !important',
                    display: 'block !important',
                    margin: '0 auto !important'
                }}>
                    <strong>A project of "THE RECOVERY HOUSE"</strong>
                </p>
            </div>
                            </td>
                        </tr>
                    </tbody>
                    <tfoot>
                        <tr>
                            <td>
                                <div className="print-footer">
                                    This is a system generated print no signature required, <strong>NOT VALID FOR COURT</strong>
                                </div>
                            </td>
                        </tr>
                    </tfoot>
                </table>
        </>
    );
}
